import { AxiosResponse } from 'axios';
import { reactive } from 'vue';

import * as Sentry from '@sentry/vue';
import { v4 as uuidv4 } from 'uuid';

import { StorableBaseService, Id, PaginatedResponse } from '../services/_base';

import { connection } from '../globals/internet';
import { isAuthenticated } from '../globals/helpers';
import { relativeTimeThreshold } from 'moment';
import { session } from '@/globals/session';

export const registry: Array<BaseStore<Id>> = new Array<BaseStore<Id>>();

export class LocalResource<T extends Id> {
  createdAt: string;
  lastSynch?: string;
  entry: T;

  constructor(entry: T, lastSynch?: string) {
    this.entry = entry;
    this.createdAt = (new Date()).toISOString();
    this.lastSynch = lastSynch;
  }
}

const LOCAL_ID_PREFIX = "$local|";

export function getLocalUuid() {
  return LOCAL_ID_PREFIX + uuidv4();
}

export enum StoreResponseError {
  NO_INTERNET = "NO_INTERNET",
  NO_REMOTE_RESOURCE = "NO_REMOTE_RESOURCE",
  NO_AUTHENTICATION = "NO_AUTHENTICATION",
  SERVER_ERROR = "SERVER_ERROR",
}

export class StoreResponse<T extends Id> {
  error?: StoreResponseError;
  resource?: LocalResource<T>;

  constructor(resource: LocalResource<T>|undefined, error?: StoreResponseError) {
    this.resource = resource;
    this.error = error;
  }
}

export enum StoreFlags {
  RETRIEVE_ALL = "RETRIEVE_ALL",
  UPLOAD_ALL = "UPLOAD_ALL",
  REMOVE_AFTER_24H = "DELETE_AFTER_24H",
}

export abstract class BaseStore<T extends Id> {
  resourceName: string;
  service: StorableBaseService<T>;
  updateInterval: number;

  updateTimer: undefined | NodeJS.Timeout;

  flags: Set<StoreFlags>;

  isUploadInProgress = false;

  $resources: Map<string, LocalResource<T>> | undefined;
  _reactiveResources: Map<string, LocalResource<T>>;

  getResourceName(): string {
    if (this.resourceName == 'base')
      throw Error("`resourceName` is not assigned in subclass");

    let prefix = 'store__';
    if (localStorage.accessToken) prefix += localStorage.accessToken.slice(0, 4) + '__';

    return prefix + this.resourceName;
  }

  hasPermissions(): boolean {
    return this.service.hasPermissions();
  }

  async update() {
    // Check permissions
    if (!this.hasPermissions()) return;

    // Check connection
    if (!connection.isConnected.value) return;

    if (this.flags.has(StoreFlags.UPLOAD_ALL)) await this.uploadAll();
    if (this.flags.has(StoreFlags.RETRIEVE_ALL)) await this.retrieveAll();
    if (this.flags.has(StoreFlags.REMOVE_AFTER_24H)) await this.removeAfter24h();
  }

  constructor(resourceName: string, service: StorableBaseService<T>, updateInterval: number, flags?: Set<StoreFlags>) {
    this.resourceName = resourceName;
    this.service = service;
    this.updateInterval = updateInterval;

    this.flags = flags || new Set([StoreFlags.RETRIEVE_ALL]);

    // Initialize reactive resources from localStorage
    this._reactiveResources = reactive(new Map());
    try {
      const key = this.getResourceName();
      const entriesRaw = localStorage.getItem(key);
      if (entriesRaw) {
        const entries = JSON.parse(entriesRaw as any);
        for (const [k, v] of entries) {
           this._reactiveResources.set(k, v);
        }
      }
    } catch (e) {
      console.warn("Failed to load initial resources", e);
    }

    // Initialize update timer
    if (this.updateInterval > 0) {
      const onUpdateError = (err: any) => {
        if (err?.response?.status === 403) return;
        Sentry.captureException(err, { tags: { source: 'BaseStore.update', resource: this.resourceName } });
      };

      this.update().catch(onUpdateError);

      this.updateTimer = setInterval(() => {
        this.update().catch(onUpdateError);
      }, this.updateInterval * 1000)
    }

    // Add to registry
    registry.push(this);
  }

  resources(): Map<string, LocalResource<T>> {
    return this._reactiveResources;
  }

  lastUpdate(): Date | undefined {
    // Load last update timestamp
    try {
      const key = this.getResourceName() + "__lastUpdate";
      const lastUpdate = localStorage.getItem(key);
      if (lastUpdate) return new Date(lastUpdate);
    } catch {
      return undefined;
    }
    return undefined;
  }

  startChanges() {
    // Create a working copy from the reactive cache
    this.$resources = new Map(this.resources());
  }

  commitChanges(wasRemoteUpdate = false) {
    if (this.$resources === undefined) throw new Error('Store is not in a change block.')

    const entryIterator = this.$resources.entries();
    const entryList = Array.from(entryIterator);
    const entriesRaw = JSON.stringify(entryList);
    const key = this.getResourceName()

    const lastUpdate = wasRemoteUpdate ? new Date() : this.lastUpdate();

    try {
      localStorage.setItem(key, entriesRaw);
      if (lastUpdate) {
        localStorage.setItem(key + "__lastUpdate", lastUpdate.toISOString());
      } else {
        localStorage.removeItem(key + "__lastUpdate");
      }
    } catch (e) {
      // localStorage may be full (QuotaExceededError) or unavailable (e.g.
      // Safari private browsing). Keep the in-memory update so the app can
      // continue; persistence will retry on the next commit.
      console.warn('[BaseStore.commitChanges] Failed to persist to localStorage', e);
    }

    // Sync changes back to reactive cache
    this._reactiveResources.clear();
    for (const [k, v] of this.$resources) {
      this._reactiveResources.set(k, v);
    }

    this.$resources = undefined;
  }

  unsynchedResources() {
    return Array.from(this.resources().values()).filter(res => !res.lastSynch);
  }

  set(resource: T, lastSynch?: string, autocommit = true): LocalResource<T> {
    if (autocommit) this.startChanges();
    if (this.$resources === undefined) throw new Error('Store is not in a change block.')

    const localResource = new LocalResource<T>(resource, lastSynch);
    this.$resources.set(resource.id, localResource);

    if (autocommit) this.commitChanges();

    return localResource;
  }

  delete(resource: T, autocommit=true) {
    if (autocommit) this.startChanges();
    if (this.$resources === undefined) throw new Error('Store is not in a change block.')

    // A remote resource can appear in multiple local resources with different
    // local resource ids (e.g. temporarily locally generated)
    const toDelete = [];
    for (const [localResourceId, localResource] of this.$resources.entries()) {
      if (localResource.entry.id == resource.id) {
        toDelete.push(localResourceId);
      }
    }

    // Delete all local resources referencing to this remote resource id
    for (const localResourceId of toDelete) this.$resources.delete(localResourceId);

    if (autocommit) this.commitChanges();
  }

  clear(autocommit=true) {
    if (autocommit) this.startChanges();
    if (this.$resources === undefined) throw new Error('Store is not in a change block.')

    this.$resources = new Map();
    if (autocommit) this.commitChanges();
  }

  clearMemory() {
    this._reactiveResources.clear();
  }

  get(id: string): LocalResource<T>|undefined {
    return this.resources().get(id);
  }

  list(): Array<LocalResource<T>> {
    return Array.from(this.resources().values());
  }

  count(): number {
    return this.resources().size;
  }

  getPaginatedListResponse(page: number): Promise<AxiosResponse<PaginatedResponse<T>>> {
    return this.service.list(page, 1000);
  }

  async retrieveAll(forceRetrieval = false): Promise<Array<T> | undefined> {
    if (!isAuthenticated()) return;

    // Only retrieve all again, if at least half of the update interval passed
    if (
      !forceRetrieval &&
      this.lastUpdate() &&
      (Number(new Date()) - Number(this.lastUpdate())) / 1000 < this.updateInterval / 2
    ) return;

    let resources: Array<T> = [];

    let page = 1;
    let response: AxiosResponse<PaginatedResponse<T>>|undefined;

    this.service.clearCache();

    while (!response || response.data.next) {
      response = await this.getPaginatedListResponse(page);
      if (!response) {
        console.log('[store.retrieveAll] No internet connection. Abort.');
        return;
      }
      resources = resources.concat(response.data.results);
      page += 1;
    }

    this.startChanges();
    this.clear(false);
    for (const resource of resources) this.set(resource, (new Date()).toISOString(), false);
    this.commitChanges(true);

    return resources;
  }

  async retrieveAllLocalOrRemote(): Promise<Array<LocalResource<T>>> {
    /*
      Return local resources if existent. Otherwise try to fetch them remotely.
      Return an empty list if the remote fetch fails.
    */

    // Empty if no permissions
    if (!this.hasPermissions()) return [];

    // Try locally
    if (this.count() > 0) return this.list();

    // Empty if no connection
    if (!connection.isConnected.value) {
      return [];
    }

    try {
      await this.retrieveAll(true);
      return Array.from(this.resources().values());
    } catch(e) {
      // Empty if request fails
      console.assert(!connection.isConnected.value, 'connection.isConnected should be false.', e);
      return [];
    }
  }

  async removeAfter24h(): Promise<undefined> {
    // Remove entries which are synched and older than 24 hours
    let localResources = this.list();

    // Filter for objects which are already synched
    localResources = localResources.filter(
        (resource: LocalResource<T>) => resource.lastSynch && resource.lastSynch > resource.createdAt);

    // Filter for all objects older than 24h
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterday_iso = yesterday.toISOString();

    localResources = localResources.filter(
      (resource: LocalResource<T>) => resource.lastSynch && resource.lastSynch < yesterday_iso);

    if (localResources.length == 0) return;

    this.startChanges();
    for (const localResource of localResources) {
      this.delete(localResource.entry, false);
    }
    this.commitChanges();
  }

  getLocalChanges(): Array<LocalResource<T>> {
    const localResources = this.list();
    localResources.sort((a, b) => a.createdAt < b.createdAt ? -1 : 1);
    return localResources.filter((resource: LocalResource<T>) => !resource.lastSynch);
  }

  async uploadAll() {
    const locallyUpdatedResources = this.getLocalChanges();

    if (locallyUpdatedResources.length == 0) return;
    if (this.isUploadInProgress) return;

    this.isUploadInProgress = true;

    this.startChanges();
    try {
      for (const resource of locallyUpdatedResources) {
        if (resource.entry.id.startsWith(LOCAL_ID_PREFIX)) {
          // Create new resources
          await this.service.create(resource.entry).then(response => {
            resource.entry = response.data;
            resource.lastSynch = (new Date()).toISOString();

            // We store the same resource in our resources map twice, once with
            // the local id ($local|<uuid>), and once with the original id
            // created by the server
            if (this.$resources === undefined) throw new Error('Store is not in a change block.')
            this.$resources.set(resource.entry.id, resource);
          });
        } else {
          // Update existing resources
          await this.service.update(resource.entry.id, resource.entry).then(response => {
            resource.entry = response.data;
            resource.lastSynch = (new Date()).toISOString();
          });
        }
      }
    } finally {
      this.commitChanges();
      this.isUploadInProgress = false;
    }
  }

  retrieve(
    id: string,
    localRetrievalFn?: (id: string) => LocalResource<T>|undefined,
    remoteRetrievalFn?: (id: string) => Promise<T>,
  ): Promise<StoreResponse<T>> {
    return new Promise(resolve => {
      let storeResponse: StoreResponse<T>;

      // Check if resource exists locally already
      let localResource;
      localRetrievalFn = localRetrievalFn || this.get.bind(this);
      localResource = localRetrievalFn(id);

      if (localResource) {
        storeResponse = new StoreResponse<T>(localResource);
        resolve(storeResponse);

        // Do not update the resource, TODO: Remove
        return;
      }

      // Try to load the resource remotely
      if (!remoteRetrievalFn) {
        remoteRetrievalFn = (id: string) => this.service.retrieve(id).then(response => {
          return response.data;
        });
      }

      remoteRetrievalFn(id).then(resource => {
        const now = (new Date()).toISOString();

        if (!storeResponse) {
          localResource = new LocalResource(resource, now);
          storeResponse = new StoreResponse<T>(localResource);
          resolve(storeResponse);
        } else if (storeResponse.resource) {
          storeResponse.resource.entry = resource;
          storeResponse.resource.lastSynch = now;
        } else {
          console.warn("store._base.BaseStore.retrieve: Invalid state reached");
        }
      }).catch(reason => {
        if (storeResponse) return;

        if (reason.response.status == 404) {
          resolve(new StoreResponse<T>(
            undefined, StoreResponseError.NO_REMOTE_RESOURCE
          ));
        } else {
          // or NO_INTERNET or NO_AUTHENTICATION or SERVER_ERROR?
          console.warn("store._base.BaseStore.retrieve", reason, reason.response.status);
        }
      });

    });
  }

}
